const createItem = ({ key, text, icon, to, aliases = [], visible = true }) => ({
  type: "item",
  key,
  text,
  icon,
  to,
  aliases,
  visible
});

const createDivider = key => ({
  type: "divider",
  key,
  visible: true
});

const createGroup = ({ key, text, icon, children = [], visible = true }) => ({
  type: "group",
  key,
  text,
  icon,
  children,
  visible
});

const isNonProduction = process.env.NODE_ENV !== "production";

const compactDividers = children =>
  children.filter((child, index, list) => {
    if (child.type !== "divider") {
      return true;
    }

    const previous = list[index - 1];
    const next = list[index + 1];

    return previous && previous.type !== "divider" && next && next.type !== "divider";
  });

const resolveNode = node => {
  if (node.visible === false) {
    return null;
  }

  if (node.type !== "group") {
    return node;
  }

  const children = compactDividers(node.children.map(resolveNode).filter(Boolean));

  if (children.length === 0) {
    return null;
  }

  return {
    ...node,
    children
  };
};

const resolveMenu = nodes => nodes.map(resolveNode).filter(Boolean);

export const getControlSoftMenu = permissions =>
  resolveMenu([
    createItem({
      key: "dashboard",
      text: "Dashboard",
      icon: "mdi-view-dashboard-outline",
      to: "/dashboard"
    }),

    createGroup({
      key: "central-clientes-operacoes",
      text: "Central de Clientes e Operações",
      icon: "mdi-account-group-outline",
      children: [
        createItem({
          key: "clientes",
          text: "Clientes",
          to: "/clientes"
        }),
        createItem({
          key: "mapa-clientes",
          text: "Mapa de Clientes",
          to: "/mapa-clientes",
          visible: Boolean(process.env.VUE_APP_ARCGIS_API_KEY)
        }),

        createDivider("central-divider-1"),

        createItem({
          key: "agenda-atualizacoes",
          text: "Agenda de Atualizações",
          to: "/atualizacao/agenda",
          aliases: ["/atualizacao/cadastro"]
        }),
        createItem({
          key: "agenda-substituicao-arquivos",
          text: "Agenda de Substituição de Arquivos",
          to: "/atualizacaoarquivos/browse",
          aliases: ["/atualizacaoarquivos/cadastro"]
        }),

        createDivider("central-divider-2"),

        createItem({
          key: "menusvisualizacao",
          text: "Configuração de Menus",
          to: "/menusclientes"
        }),
        createItem({
          key: "menusclientes",
          text: "Utilização dos Menus",
          to: "/menusvisualizacao"
        }),

        createDivider("central-divider-modulos"),

        createItem({
          key: "modulosclientes",
          text: "Configuração de Módulos",
          to: "/modulosclientes"
        }),
        createItem({
          key: "modulosvisualizacao",
          text: "Utilização dos Módulos",
          to: "/modulosvisualizacao"
        }),

        ...(process.env.NODE_ENV === "production"
          ? [
              createDivider("central-divider-empresas"),

              createItem({
                key: "empresasclientes",
                text: "Configurações de Empresas",
                to: "/empresasclientes"
              })
            ]
          : []),

        createDivider("central-divider-3"),

        createItem({
          key: "autenticacoes-mensais",
          text: "Autenticações Mensais",
          to: "/autenticacao-monitor"
        }),
        createItem({
          key: "notificacoes",
          text: "Notificações",
          to: "/agronotificacao"
        }),

        ...(process.env.NODE_ENV === "production"
          ? [
              createDivider("central-divider-4"),

              createItem({
                key: "db-storage",
                text: "DBStorage",
                to: "/dbstorage"
              })
            ]
          : [])
      ]
    }),

    createGroup({
      key: "consultas-processamentos",
      text: "Consultas e Processamentos",
      icon: "mdi-magnify",
      children: [
        createItem({
          key: "acompanhamento-safra",
          text: "Acompanhamento de Safra",
          to: "/acompanhamento-safra",
          visible: permissions.hasProject257
        }),
        createItem({
          key: "acompanhamento-sysgrao",
          text: "Acompanhamento de Romaneios do SySGrão",
          to: "/acompanhamento-sysgrao"
        }),
        createItem({
          key: "produtos-web-mobile-consulta",
          text: "Produtos Web/Mobile",
          to: "/consulta/produtoswebmobile"
        }),
        createItem({
          key: "monitor-agroweb",
          text: "Monitor do Agro.web",
          to: "/monitor-agroweb"
        }),
        createItem({
          key: "monitor-syssupervisor",
          text: "Monitor do SySSupervisor",
          to: "/monitor-syssupervisor"
        }),

        createDivider("consultas-divider-1"),

        createItem({
          key: "datahub",
          text: "DataHub",
          to: "/datahub",
          aliases: ["/datahub/cadastro", "/datahub/visualizacao"],
          visible: permissions.hasProject257
        })
      ]
    }),

    createGroup({
      key: "integracoes",
      text: "Integrações",
      icon: "mdi-power-plug-outline",
      visible: permissions.hasProject257,
      children: [
        createItem({
          key: "integracoes-romaneios-clientes",
          text: "Romaneios entre Clientes",
          to: "/integracoes/romaneios-clientes",
          aliases: ["/integracoes/romaneios-clientes-consulta"]
        }),
        createItem({
          key: "integracoes-pesador-offline",
          text: "Pesador Offline",
          to: "/integracoes/pesador-offline",
          aliases: ["/integracoes/pesadorconsulta", "/integracoes/pesadorligacao"]
        }),
        createItem({
          key: "integracoes-lar",
          text: "Lar",
          to: "/integracoes/lar",
          aliases: ["/integracoes/larconsulta"]
        })
      ]
    }),

    createGroup({
      key: "monitoramentos",
      text: "Monitoramentos",
      icon: "mdi-pulse",
      children: [
        createItem({
          key: "cs-api",
          text: "APIs",
          to: "/csapi/admin-instrument"
        }),
        createItem({
          key: "status-servidores",
          text: "Status do CSServer",
          to: "/server/status"
        }),
        createItem({
          key: "logs-sistema",
          text: "Logs do Sistema",
          to: "/server/logs"
        }),
        createItem({
          key: "erros",
          text: "Erros",
          to: "/erros"
        }),
        createItem({
          key: "analisegerencial",
          text: "Análise Gerencial",
          to: "/analise_ia_gerencial"
        })
      ]
    }),

    createGroup({
      key: "databridge",
      text: "DataBridge",
      icon: "mdi-database-outline",
      children: [
        createItem({
          key: "databridge-instituicoes-financeiras",
          text: "Instituições Financeiras",
          to: "/databrigde/instituicoes-financeiras"
        }),
        createItem({
          key: "databridge-reforma-tributaria",
          text: "Reforma Tributária",
          to: "/databrigde/reforma-tributaria"
        }),
        createItem({
          key: "databridge-base-conhecimento",
          text: "Base de Conhecimento",
          to: "/databrigde/base-conhecimento"
        }),
        createItem({
          key: "databridge-faq",
          text: "FAQ",
          to: "/databrigde/faq"
        }),
        createItem({
          key: "databridge-reinf",
          text: "REINF",
          to: "/databrigde/reinf"
        }),
        createItem({
          key: "databridge-adapar",
          text: "ADAPAR",
          to: "/databrigde/adapar"
        }),
        createItem({
          key: "databridge-indea",
          text: "INDEA",
          to: "/databrigde/indea"
        }),
        createItem({
          key: "databridge-bayer",
          text: "BAYER",
          to: "/databrigde/bayer"
        }),
        createItem({
          key: "databridge-nexxera",
          text: "NEXXERA",
          to: "/databrigde/nexxera"
        }),
        createItem({
          key: "databridge-soft-house",
          text: "Software House",
          to: "/databrigde/responsavel-tecnico"
        }),
        ...(isNonProduction
          ? [
              createItem({
                key: "databridge-auditoria",
                text: "Auditoria",
                to: "/databrigde/auditoria"
              })
            ]
          : [])
      ]
    }),

    createGroup({
      key: "administracao-tecnica",
      text: "Administração Técnica",
      icon: "mdi-wrench-outline",
      children: [
        createItem({
          key: "dashboard",
          text: "Versões",
          to: "/versoes"
        }),
        createItem({
          key: "auditoria",
          text: "Auditoria",
          to: "/auditoria"
        }),

        createDivider("administracao-divider-1"),

        createItem({
          key: "menuscadastro",
          text: "Cadastro de Menu do Sistema",
          to: "/menuscadastro"
        }),

        createDivider("administracao-divider-2"),

        createItem({
          key: "dicionario-tradutor",
          text: "Dicionário - Tradutor",
          to: "/dicionario-tradutor",
          visible: permissions.hasProject306
        })
      ]
    })
  ]);

export const getSysSafraMenu = () =>
  resolveMenu([
    createGroup({
      key: "sysbackup",
      text: "SySBackup",
      icon: "fa-solid fa-shield",
      children: [
        createItem({
          key: "sysbackup-monitor",
          text: "Monitor",
          to: "/sysafra/sysbackup/monitor",
          aliases: ["/sysafra/sysbackup/solicitacoes"]
        }),
        createItem({
          key: "sysbackup-validacoes",
          text: "Validações",
          to: "/sysafra/sysbackup/validacoes"
        }),
        createItem({
          key: "sysbackup-clientes",
          text: "Clientes",
          to: "/sysafra/sysbackup/clientes"
        }),
        createItem({
          key: "sysbackup-configuracoes",
          text: "Configurações",
          to: "/sysafra/sysbackup/config"
        })
      ]
    })
  ]);

export default {
  getControlSoftMenu,
  getSysSafraMenu
};
